import React, { useState } from 'react';
import { vacationsAPI } from '../../api/api';
import Modal from '../Common/Modal';
import { useLanguage } from '../../context/LanguageContext';

// Public holidays of one year. Everyone can see them; admins add/remove
// them, or fill in the common Swiss ones in one click (cantonal holidays
// differ, so the list is a starting point to adjust).

const pad = (n) => String(n).padStart(2, '0');
const key = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

// Easter Sunday (anonymous Gregorian algorithm).
const easter = (y) => {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(y, month - 1, day);
};

export const swissHolidays = (y) => {
  const e = easter(y);
  return [
    { date: `${y}-01-01`, name: 'Neujahr' },
    { date: `${y}-01-02`, name: 'Berchtoldstag' },
    { date: key(addDays(e, -2)), name: 'Karfreitag' },
    { date: key(addDays(e, 1)), name: 'Ostermontag' },
    { date: key(addDays(e, 39)), name: 'Auffahrt' },
    { date: key(addDays(e, 50)), name: 'Pfingstmontag' },
    { date: `${y}-08-01`, name: 'Bundesfeier' },
    { date: `${y}-12-25`, name: 'Weihnachten' },
    { date: `${y}-12-26`, name: 'Stephanstag' },
  ];
};

const HolidaysModal = ({ isOpen, onClose, year, holidays, isAdmin, onChanged }) => {
  const { t, language } = useLanguage();
  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const ofYear = holidays.filter((h) => h.date.startsWith(`${year}-`));

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch (err) {
      alert(t('vacations.failedToSave', { error: err.response?.data?.error || err.message }));
    } finally {
      setBusy(false);
    }
  };

  const add = (e) => {
    e.preventDefault();
    run(async () => {
      await vacationsAPI.addHolidays([{ date, name }]);
      setDate('');
      setName('');
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`🎉 ${t('vacations.holidaysTitle', { year })}`} size='sm'>
      <div className='space-y-4'>
        {ofYear.length === 0 ? (
          <p className='text-sm text-gray-500'>{t('vacations.noHolidays')}</p>
        ) : (
          <ul className='divide-y divide-gray-100'>
            {ofYear.map((h) => {
              const d = new Date(`${h.date}T00:00:00`);
              return (
                <li key={h.id} className='flex items-center justify-between py-2 text-sm'>
                  <span>
                    <span className='inline-block w-28 text-gray-500'>
                      {d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: '2-digit' })}
                    </span>
                    <span className='font-medium text-red-700'>{h.name}</span>
                  </span>
                  {isAdmin && (
                    <button
                      onClick={() => run(() => vacationsAPI.deleteHoliday(h.id))}
                      disabled={busy}
                      className='text-xs text-red-600 hover:text-red-800'
                    >
                      {t('vacations.delete')}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {isAdmin && (
          <>
            <form onSubmit={add} className='flex flex-wrap items-end gap-2 border-t border-gray-100 pt-4'>
              <input
                type='date'
                value={date}
                min={`${year}-01-01`}
                max={`${year}-12-31`}
                onChange={(e) => setDate(e.target.value)}
                className='input-field w-auto'
                required
              />
              <input
                type='text'
                value={name}
                onChange={(e) => setName(e.target.value)}
                className='input-field flex-1 min-w-[120px]'
                placeholder={t('vacations.holidayName')}
                maxLength={100}
                required
              />
              <button type='submit' className='btn-primary' disabled={busy}>
                {t('vacations.add')}
              </button>
            </form>
            <button
              onClick={() => run(() => vacationsAPI.addHolidays(swissHolidays(year)))}
              disabled={busy}
              className='btn-secondary w-full'
            >
              🇨🇭 {t('vacations.addSwissHolidays', { year })}
            </button>
            <p className='text-xs text-gray-500'>{t('vacations.swissHolidaysHint')}</p>
          </>
        )}
      </div>
    </Modal>
  );
};

export default HolidaysModal;
